import { Box, Dialog, DialogActions, DialogContent, IconButton, Typography } from "@mui/material"
import { CloseOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React from "react"

import { Button } from "@/components/common/Button"
import type { Role } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useRole } from "@/screens/SystemAdmin/hooks"
import { SystemAdminDialog } from "@/screens/SystemAdmin/types"
import { formatRoleUserInfo } from "@/screens/SystemAdmin/utils"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface DeleteRoleDialogProps {
  isOpen: SystemAdminDialog | null
  role: Role
  onClose: () => void
  onSuccess: () => void
}

const RESERVED_ROLES = ["New User", "Administrator", "Super Administrator"]

const DeleteRoleDialog: FC<DeleteRoleDialogProps> = ({ isOpen, onClose, role, onSuccess }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { deleteRole } = useRole()
  const hasRoleUsers = Boolean(role?.users?.length)

  const handleDelete = async () => {
    const { error, data } = await deleteRole({
      input: { roleId: role.roleId, isDeleted: true },
    })

    const hasError =
      Boolean(error?.graphQLErrors.length) ||
      (data?.role.delete.__typename === "RoleFailure" && Boolean(data?.role.delete.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.graphQLErrors[0]?.message ?? "Failed to delete role.",
      })
      onClose()
    } else {
      notify({
        type: NotificationType.Success,
        silent: false,
        content: "Role successfully deleted.",
      })
      onSuccess()
    }
  }

  const handleCancel = () => {
    onClose()
  }

  const defaultContent = (
    <>
      <DialogContent classes={{ root: "p-10" }}>
        <Box textAlign='center'>
          <WarningOutlined className='mb-4 size-[48px] text-primary' />
          <Typography variant='h5' color='primary' className='mt-6 font-normal'>
            You are attempting to delete a Role
          </Typography>
          <Typography variant='h6' className='mt-6 px-3 text-base leading-[150%] tracking-[-0.85px] text-gray-700'>
            You will no longer have access to this role.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={handleCancel}>
          Go Back
        </Button>
        <Button onClick={handleDelete}>Continue</Button>
      </DialogActions>
    </>
  )

  const hasRoleUsersContent = (
    <>
      <DialogContent classes={{ root: "p-10" }}>
        <Box textAlign='center'>
          <WarningOutlined className='mb-4 size-[48px] text-red-600' />
          <Typography variant='h5' color='primary' className='mt-6 font-normal'>
            You are attempting to delete a Role and Permissions that has associated users
          </Typography>
          <Typography variant='h6' className='mt-6 px-3 text-base leading-[150%] tracking-[-0.85px] text-gray-700'>
            You will need to remove the role from these users before you are able to delete the role and permissions:
            <br />
            {formatRoleUserInfo(role?.users ?? [])}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions className='justify-end px-10 pb-10'>
        <Button onClick={handleDelete} variant='error'>
          Ok, Continue
        </Button>
      </DialogActions>
    </>
  )

  const reservedRoleContent = (
    <>
      <DialogContent classes={{ root: "p-10" }}>
        <Box textAlign='center'>
          <WarningOutlined className='mb-4 size-[48px] text-red-600' />

          <Typography variant='h5' color='primary' className='mt-6 font-normal'>
            You are attempting to delete a system Role
          </Typography>
          <Typography variant='h6' className='mt-6 px-3 text-base leading-[150%] tracking-[-0.85px] text-gray-700'>
            This role cannot be removed from the system
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions className='justify-end px-10 pb-10'>
        <Button onClick={handleDelete} variant='error'>
          Ok, Continue
        </Button>
      </DialogActions>
    </>
  )

  const renderContent = () => {
    switch (true) {
      case RESERVED_ROLES.some((v) => v === role.name):
        return reservedRoleContent
      case hasRoleUsers:
        return hasRoleUsersContent
      default:
        return defaultContent
    }
  }

  return (
    <Dialog open={isOpen === SystemAdminDialog.DELETE_ROLE} onClose={handleCancel} fullWidth>
      <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
        <CloseOutlined />
      </IconButton>
      {renderContent()}
    </Dialog>
  )
}

export default DeleteRoleDialog
